/**
 * CertificatesPage.jsx
 * Sección privada: lista de certificados del usuario autenticado.
 * Ruta: /home/certificates
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import { Award, ExternalLink, Loader, AlertCircle } from 'lucide-react';

export default function CertificatesPage() {
  const navigate = useNavigate();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/badges/my-list')
      .then(res => setBadges(res.data.badges || []))
      .catch(() => setError('No se pudieron cargar los certificados.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-16 text-slate-500">
        <Loader className="w-5 h-5 animate-spin text-cyan-500" />
        <span className="text-sm">Cargando certificados...</span>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Award className="w-5 h-5 text-cyan-600" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Mis certificados</h1>
        </div>
        <p className="text-sm text-slate-500">Insignias digitales obtenidas en EduTech Pulse Lab.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-6 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {!error && badges.length === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl">
          <Award className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600">Aún no tienes certificados</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Completa un programa y reclama tu primera insignia.</p>
          <button
            onClick={() => navigate('/programas')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg text-sm transition-colors"
          >
            Ver programas disponibles
          </button>
        </div>
      )}

      {badges.length > 0 && (
        <div className="space-y-4">
          {badges.map(badge => (
            <div
              key={badge.verificationId}
              className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-cyan-300 transition-colors"
            >
              {/* Icono */}
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                <Award className="w-6 h-6 text-cyan-600" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-cyan-600 mb-0.5">
                  {badge.badgeType === 'completion' ? 'Completion badge' : badge.badgeType}
                </p>
                <h3 className="text-sm font-bold text-slate-900 leading-snug truncate">
                  {badge.badgeLabelSnapshot}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{badge.programTitleSnapshot}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Emitida el {new Date(badge.claimedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              {/* CTAs */}
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate(`/home/certificates/${badge.verificationId}`)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg text-xs transition-colors"
                >
                  <Award className="w-3.5 h-3.5" />
                  Ver detalle
                </button>
                <a
                  href={`/badge/${badge.verificationId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg text-xs transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Verificar
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
